import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FaArrowDown, FaArrowUp, FaSave, FaSpinner, FaTimes } from 'react-icons/fa';
import { CARRIERS, SHIPMENT_STATUS, parseShipmentCost } from '../../../utils/shipments';
import {
  CARGO_DIRECTION,
  CARGO_DIRECTION_OPTIONS,
  InsufficientStockError,
  getCargoDirection,
  getCargoStatusOptions,
  normalizeCargoItems,
} from '../../../utils/companyShipments';
import { datesForStatus } from './useCompanyShipments';
import CargoItemsField from './CargoItemsField';
import CargoAttachmentsField from './CargoAttachmentsField';
import styles from './CargoTracking.module.css';

const emptyForm = {
  direction: CARGO_DIRECTION.OUTGOING,
  subject: '',
  content: '',
  status: SHIPMENT_STATUS.NOT_SENT,
  carrier: '',
  trackingNumber: '',
  shippedAt: '',
  expectedAt: '',
  deliveredAt: '',
  cost: '',
  partyName: '',
  partyOrg: '',
  partyPhone: '',
  address: '',
  items: [],
  attachments: [],
  note: '',
};

const DIRECTION_ICONS = {
  [CARGO_DIRECTION.INCOMING]: FaArrowDown,
  [CARGO_DIRECTION.OUTGOING]: FaArrowUp,
};

const Field = ({ id, label, required, error, children }) => (
  <div className={styles.field}>
    <label htmlFor={id}>
      {label} {required && <span className={styles.required}>*</span>}
    </label>
    {children}
    {error && <span className={styles.errorText}>{error}</span>}
  </div>
);

/** Firma kargosu ekleme / düzenleme formu. Kaydetme çağıran tarafa bırakılır. */
const CargoFormModal = ({ show, onHide, shipment, initialDirection, stocks, onSave }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!show) return;
    setErrors({});
    setForm(
      shipment
        ? {
            ...emptyForm,
            ...shipment,
            cost: shipment.cost == null ? '' : String(shipment.cost).replace('.', ','),
          }
        : { ...emptyForm, direction: initialDirection || emptyForm.direction }
    );
  }, [show, shipment, initialDirection]);

  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e) => e.key === 'Escape' && !saving && onHide();
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [show, saving, onHide]);

  if (!show) return null;

  const direction = getCargoDirection(form.direction);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleStatusChange = (status) =>
    setForm((prev) => ({ ...prev, status, ...datesForStatus(prev, status) }));

  const validate = () => {
    const next = {};
    if (!form.subject.trim()) next.subject = 'Konu başlığı zorunludur';
    if (!form.partyName.trim()) next.partyName = `${direction.partyLabel} zorunludur`;
    if (form.status !== SHIPMENT_STATUS.NOT_SENT && !form.carrier) {
      next.carrier = 'Kargo yola çıktıysa firma seçin';
    }
    if (Number.isNaN(parseShipmentCost(form.cost))) {
      next.cost = 'Geçerli bir tutar girin (örn. 125,50)';
    }
    if (form.items.some((item) => item.stockId && !(parseFloat(item.quantity) > 0))) {
      next.items = 'Seçilen her stok için adet girin';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (uploading) {
      toast.error('Dosya yüklemesi bitene kadar bekleyin');
      return;
    }
    if (!validate()) return;

    setSaving(true);
    try {
      // Yalnızca form alanları yazılır; id/history/appliedItems gibi kayıt
      // alanları kaydetme katmanında yönetilir.
      const payload = Object.keys(emptyForm).reduce(
        (acc, key) => ({ ...acc, [key]: form[key] }),
        {}
      );
      await onSave({
        ...payload,
        subject: form.subject.trim(),
        partyName: form.partyName.trim(),
        trackingNumber: form.trackingNumber.trim(),
        cost: parseShipmentCost(form.cost),
        items: normalizeCargoItems(form.items),
      });
      onHide();
    } catch (error) {
      if (error instanceof InsufficientStockError) {
        setErrors((prev) => ({ ...prev, items: error.message }));
      }
      // Bildirim çağıran tarafta gösteriliyor
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.dialogOverlay} onClick={() => !saving && onHide()}>
      <div
        className={styles.dialogCard}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cargo-form-title"
      >
        <header className={styles.dialogHeader}>
          <h2 id="cargo-form-title">{shipment ? 'Kargo Kaydını Düzenle' : 'Yeni Kargo Kaydı'}</h2>
          <button type="button" className={styles.squareButton} onClick={onHide} disabled={saving} aria-label="Kapat">
            <FaTimes />
          </button>
        </header>

        <div className={styles.dialogBody}>
          <div className={styles.directionPicker}>
            {CARGO_DIRECTION_OPTIONS.map((option) => {
              const Icon = DIRECTION_ICONS[option.value];
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.directionOption} ${styles[`dir_${option.value}`]} ${
                    form.direction === option.value ? styles.directionOptionActive : ''
                  }`}
                  onClick={() => setField('direction', option.value)}
                  aria-pressed={form.direction === option.value}
                >
                  <span className={styles.directionIcon}><Icon /></span>
                  <span>
                    <strong>{option.label}</strong>
                    <small>{option.description}</small>
                  </span>
                </button>
              );
            })}
          </div>

          <section className={styles.formSection}>
            <h3>Gönderi</h3>
            <Field id="cargoSubject" label="Konu Başlığı" required error={errors.subject}>
              <input
                id="cargoSubject"
                className={`${styles.control} ${errors.subject ? styles.controlError : ''}`}
                value={form.subject}
                onChange={(e) => setField('subject', e.target.value)}
                placeholder="Örn. Tedarikçiden lab kiti sevkiyatı"
                maxLength={120}
              />
            </Field>
            <Field id="cargoContent" label="İçerik">
              <textarea
                id="cargoContent"
                className={styles.control}
                rows={2}
                value={form.content}
                onChange={(e) => setField('content', e.target.value)}
                placeholder="Kutuda ne var? Malzeme, adet, lot numarası..."
              />
            </Field>
          </section>

          <section className={styles.formSection}>
            <h3>Durum ve Kargo Bilgileri</h3>
            <div className={styles.statusPicker}>
              {getCargoStatusOptions(form.direction).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.statusOption} ${styles[`tone_${option.tone}`]} ${
                    form.status === option.value ? styles.statusOptionActive : ''
                  }`}
                  onClick={() => handleStatusChange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className={styles.fieldRow}>
              <Field id="cargoCarrier" label="Kargo Firması" error={errors.carrier}>
                <select
                  id="cargoCarrier"
                  className={`${styles.control} ${errors.carrier ? styles.controlError : ''}`}
                  value={form.carrier}
                  onChange={(e) => setField('carrier', e.target.value)}
                >
                  <option value="">Seçiniz...</option>
                  {CARRIERS.map((carrier) => (
                    <option key={carrier.value} value={carrier.value}>{carrier.label}</option>
                  ))}
                </select>
              </Field>
              <Field id="cargoTracking" label="Takip No">
                <input
                  id="cargoTracking"
                  className={styles.control}
                  value={form.trackingNumber}
                  onChange={(e) => setField('trackingNumber', e.target.value)}
                  placeholder="Takip numarası"
                />
              </Field>
              <Field id="cargoCost" label="Kargo Bedeli (₺)" error={errors.cost}>
                <input
                  id="cargoCost"
                  inputMode="decimal"
                  className={`${styles.control} ${errors.cost ? styles.controlError : ''}`}
                  value={form.cost}
                  onChange={(e) => setField('cost', e.target.value)}
                  placeholder="0,00"
                />
              </Field>
            </div>
            <div className={styles.fieldRow}>
              <Field id="cargoShippedAt" label="Gönderim Tarihi">
                <input id="cargoShippedAt" type="date" className={styles.control} value={form.shippedAt}
                  onChange={(e) => setField('shippedAt', e.target.value)} />
              </Field>
              <Field id="cargoExpectedAt" label="Beklenen Teslim">
                <input id="cargoExpectedAt" type="date" className={styles.control} value={form.expectedAt}
                  min={form.shippedAt || undefined}
                  onChange={(e) => setField('expectedAt', e.target.value)} />
              </Field>
              <Field id="cargoDeliveredAt" label="Teslim Tarihi">
                <input id="cargoDeliveredAt" type="date" className={styles.control} value={form.deliveredAt}
                  min={form.shippedAt || undefined}
                  onChange={(e) => setField('deliveredAt', e.target.value)} />
              </Field>
            </div>
          </section>

          <section className={styles.formSection}>
            <h3>{direction.partyLabel}</h3>
            <div className={styles.fieldRow}>
              <Field id="cargoParty" label={`${direction.partyLabel} Adı`} required error={errors.partyName}>
                <input
                  id="cargoParty"
                  className={`${styles.control} ${errors.partyName ? styles.controlError : ''}`}
                  value={form.partyName}
                  onChange={(e) => setField('partyName', e.target.value)}
                  placeholder="Ad soyad veya firma"
                />
              </Field>
              <Field id="cargoPartyOrg" label="Kurum / Merkez">
                <input id="cargoPartyOrg" className={styles.control} value={form.partyOrg}
                  onChange={(e) => setField('partyOrg', e.target.value)} placeholder="Hastane, tedarikçi..." />
              </Field>
              <Field id="cargoPartyPhone" label="Telefon">
                <input id="cargoPartyPhone" type="tel" className={styles.control} value={form.partyPhone}
                  onChange={(e) => setField('partyPhone', e.target.value)} placeholder="05xx xxx xx xx" />
              </Field>
            </div>
            <Field id="cargoAddress" label={direction.addressLabel}>
              <textarea id="cargoAddress" className={styles.control} rows={2} value={form.address}
                onChange={(e) => setField('address', e.target.value)} placeholder="Adres" />
            </Field>
          </section>

          <section className={styles.formSection}>
            <h3>Stok Kalemleri</h3>
            <CargoItemsField
              items={form.items}
              onChange={(items) => setField('items', items)}
              stocks={stocks}
              direction={form.direction}
              error={errors.items}
            />
          </section>

          <section className={styles.formSection}>
            <h3>Dosyalar ve Not</h3>
            <CargoAttachmentsField
              attachments={form.attachments}
              onChange={(attachments) => setField('attachments', attachments)}
              onUploadingChange={setUploading}
            />
            <Field id="cargoNote" label="Not">
              <textarea id="cargoNote" className={styles.control} rows={2} value={form.note}
                onChange={(e) => setField('note', e.target.value)} placeholder="Eklemek istediğiniz not" />
            </Field>
          </section>
        </div>

        <footer className={styles.dialogFooter}>
          <button type="button" className={styles.outlineButton} onClick={onHide} disabled={saving}>
            Vazgeç
          </button>
          <button type="button" className={styles.primaryButton} onClick={handleSubmit} disabled={saving || uploading}>
            {saving ? <FaSpinner className={styles.spin} /> : <FaSave />}
            {saving ? 'Kaydediliyor...' : shipment ? 'Güncelle' : 'Kaydet'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default CargoFormModal;
